import { api } from "@/lib/api";
import { device, kvGet, kvSet } from "@/lib/localdb";

// Per device + branch blocks of real receipt numbers, reserved while online and used by offline sales.
const KEEP = 25;
const key = (branchId) => `blocks:${branchId}`;
const load = async (branchId) => (await kvGet(key(branchId))) || { ranges: [], tempSeq: 0 };
const left = (st) => st.ranges.reduce((a, r) => a + Math.max(0, r.end - r.next + 1), 0);
const pad = (n) => String(n).padStart(6, "0");
const inflight = {};

export function ensureBlock(branchId) {
  if (inflight[branchId]) return inflight[branchId];
  inflight[branchId] = (async () => {
    const st = await load(branchId);
    if (left(st) >= KEEP) return;
    const { data } = await api.post("/receipts/reserve", { device_id: device().id }, { headers: { "X-Branch-Id": branchId } });
    const cur = await load(branchId);
    cur.ranges.push({ start: data.start, end: data.end, next: data.start });
    await kvSet(key(branchId), cur);
  })()
    .catch(() => {})
    .finally(() => delete inflight[branchId]);
  return inflight[branchId];
}

function pick(st, branch) {
  const r = st.ranges.find((x) => x.next <= x.end);
  if (r) return { r, receipt_no: r.next, receipt_code: `${branch.code}-${pad(r.next)}` };
  return { receipt_no: null, receipt_code: `${branch.code}-T${device().code}-${st.tempSeq + 1}` };
}

export async function peekNumber(branch) {
  return pick(await load(branch.id), branch).receipt_code;
}

export async function takeNumber(branch) {
  const st = await load(branch.id);
  const p = pick(st, branch);
  if (p.r) p.r.next += 1;
  else st.tempSeq += 1;
  st.ranges = st.ranges.filter((x) => x.next <= x.end);
  await kvSet(key(branch.id), st);
  return { receipt_no: p.receipt_no, receipt_code: p.receipt_code };
}

export async function blockState(branchId) {
  const st = await load(branchId);
  return { ...st, remaining: left(st) };
}

// Test helper: use up the reserved numbers so the next offline sale gets a temporary number.
export async function exhaustBlocks(branchId) {
  const st = await load(branchId);
  st.ranges = [];
  await kvSet(key(branchId), st);
}

// Drops this device's local blocks for a branch (e.g. after the server says a number wasn't reserved here).
export async function resetBlocks(branchId) {
  const st = await load(branchId);
  await kvSet(key(branchId), { ...st, ranges: [] });
}
